/**
 * Add debug block commands dropdown to the menu to directly execute ProseMirror block-level commands.
 */
import {
    joinUp,
    joinDown,
    joinForward,
    joinBackward,
    joinTextblockBackward,
    joinTextblockForward,
    lift,
    splitBlock,
    liftEmptyBlock,
    createParagraphNear,
} from "prosemirror-commands";
import { getController } from "@/wc/text-editor/lib/core/plugins/host-events.js";
import { BaseExtension } from "@/wc/text-editor/lib/core/base-extension";
import type { MenuFunction, MenuItem } from "@/wc/text-editor/lib/core/menu";

export class DebugMenuExtension extends BaseExtension {
    menu =
        (currentMenu: MenuFunction): MenuFunction =>
        (view) => [
            ...currentMenu(view),
            {
                label: "Debug",
                name: "debug",
                type: "dropdown",
                group: "debug",
                items: [
                    {
                        label: "Dump JSON selection",
                        group: "debug",
                        name: "dumpJsonSelection",
                        run: (state, _, view) => {
                            const slice = state.doc.slice(
                                view.state.selection.from,
                                view.state.selection.to
                            );
                            console.log(
                                JSON.stringify(slice.toJSON(), undefined, 2)
                            );
                            view.focus();
                        },
                        active: () => false,
                        type: "toggle",
                    },
                    {
                        label: "Dump JSON state",
                        group: "debug",
                        name: "dumpJsonState",
                        run: (state, _, view) => {
                            console.log(
                                JSON.stringify(state.toJSON(), undefined, 2)
                            );
                            view.focus();
                        },
                        active: () => false,
                        type: "toggle",
                    },
                    {
                        label: "Dump Markdown doc",
                        group: "debug",
                        name: "dumpMardownDoc",
                        run: (state, _, view) => {
                            const controller = getController(state);
                            console.log(controller.serialize());
                            view.focus();
                        },
                        active: () => false,
                        type: "toggle",
                    },
                    {
                        label: "Dump schema",
                        group: "debug",
                        name: "dumpSchema",
                        run: (state, _, view) => {
                            const controller = getController(state);
                            console.log(controller._view?.state.schema.spec);
                            view.focus();
                        },
                        active: () => false,
                        type: "toggle",
                    },
                    {
                        label: "joinUp",
                        group: "debug",
                        name: "joinUp",
                        disabled: !joinUp(view.state),
                        run: (state, dispatch, view) => {
                            joinUp(state, dispatch);
                            view.focus();
                        },
                        active: () => false,
                        type: "toggle",
                    },
                    {
                        label: "joinDown",
                        group: "debug",
                        name: "joinDown",
                        disabled: !joinDown(view.state),
                        run: (state, dispatch, view) => {
                            joinDown(state, dispatch);
                            view.focus();
                        },
                        active: () => false,
                        type: "toggle",
                    },
                    {
                        label: "joinBackward",
                        group: "debug",
                        name: "joinBackward",
                        disabled: !joinBackward(view.state),
                        run: (state, dispatch, view) => {
                            joinBackward(state, dispatch);
                            view.focus();
                        },
                        active: () => false,
                        type: "toggle",
                    },
                    {
                        label: "joinForward",
                        group: "debug",
                        name: "joinForward",
                        disabled: !joinForward(view.state),
                        run: (state, dispatch, view) => {
                            joinForward(state, dispatch);
                            view.focus();
                        },
                        active: () => false,
                        type: "toggle",
                    },
                    {
                        label: "joinTextblockBackward",
                        group: "debug",
                        name: "joinTextblockBackward",
                        disabled: !joinTextblockBackward(view.state),
                        run: (state, dispatch, view) => {
                            joinTextblockBackward(state, dispatch);
                            view.focus();
                        },
                        active: () => false,
                        type: "toggle",
                    },
                    {
                        label: "joinTextblockForward",
                        group: "debug",
                        name: "joinTextblockForward",
                        disabled: !joinTextblockForward(view.state),
                        run: (state, dispatch, view) => {
                            joinTextblockForward(state, dispatch);
                            view.focus();
                        },
                        active: () => false,
                        type: "toggle",
                    },
                    {
                        label: "lift",
                        group: "debug",
                        name: "lift",
                        disabled: !lift(view.state),
                        run: (state, dispatch, view) => {
                            lift(state, dispatch);
                            view.focus();
                        },
                        active: () => false,
                        type: "toggle",
                    },
                    {
                        label: "splitBlock",
                        group: "debug",
                        name: "splitBlock",
                        disabled: !splitBlock(view.state),
                        run: (state, dispatch, view) => {
                            splitBlock(state, dispatch);
                            view.focus();
                        },
                        active: () => false,
                        type: "toggle",
                    },
                    {
                        label: "liftEmptyBlock",
                        group: "debug",
                        name: "liftEmptyBlock",
                        disabled: !liftEmptyBlock(view.state),
                        run: (state, dispatch, view) => {
                            liftEmptyBlock(state, dispatch);
                            view.focus();
                        },
                        active: () => false,
                        type: "toggle",
                    },
                    {
                        label: "createParagraphNear",
                        group: "debug",
                        name: "createParagraphNear",
                        disabled: !createParagraphNear(view.state),
                        run: (state, dispatch, view) => {
                            createParagraphNear(state, dispatch);
                            view.focus();
                        },
                        active: () => false,
                        type: "toggle",
                    },
                    {
                        label: "liftEmptyBlock",
                        group: "debug",
                        name: "liftEmptyBlock",
                        disabled: !liftEmptyBlock(view.state),
                        run: (state, dispatch, view) => {
                            liftEmptyBlock(state, dispatch);
                            view.focus();
                        },
                        active: () => false,
                        type: "toggle",
                    },
                ] as MenuItem[],
            },
        ];
}
